<template>
  <section v-if="items.length || loaded === false" class="ob" aria-label="Письма на подтверждение">
    <header class="ob__head">
      <div>
        <h2 class="ob__title">Письма на подтверждение <span v-if="drafts" class="ob__badge">{{ drafts }}</span></h2>
        <p class="ob__lead">
          Система собрала письма заказчику. Пока идёт тестовый режим, письмо уходит только после того,
          как руководитель нажмёт «Отправить».
        </p>
      </div>
      <button class="ob__refresh" type="button" :disabled="pending" @click="load">Обновить</button>
    </header>

    <p v-if="error" class="ob__error">{{ error }}</p>

    <article v-for="row in items" :key="row.id" :class="['ob__card', 'ob__card--' + row.status]">
      <div class="ob__top">
        <span class="ob__kind">{{ kindTitle(row.kind) }}</span>
        <span class="ob__trip">Рейс {{ row.trip_id }}</span>
        <span :class="['ob__status', 'ob__status--' + row.status]">{{ statusTitle(row.status) }}</span>
      </div>
      <p class="ob__to"><b>Кому:</b> {{ row.recipients }}</p>
      <p class="ob__subject"><b>Тема:</b> {{ row.subject }}</p>
      <details class="ob__text" :open="row.status === 'draft'">
        <summary>Текст письма</summary>
        <pre>{{ row.body }}</pre>
      </details>
      <p v-if="row.error" class="ob__error">Ошибка отправки: {{ row.error }}</p>
      <p v-if="row.decided_by" class="ob__who">Решение: {{ row.decided_by }}</p>
      <div v-if="canApprove && (row.status === 'draft' || row.status === 'failed')" class="ob__actions">
        <button class="ob__btn ob__btn--send" type="button" :disabled="busyId === row.id" @click="decide(row, true)">
          Отправить
        </button>
        <button class="ob__btn" type="button" :disabled="busyId === row.id" @click="decide(row, false)">
          Не отправлять
        </button>
      </div>
    </article>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { decideOutbox, fetchOutbox } from '@/backend/confirmApi'
import { OUTBOX_KIND, OUTBOX_STATUS } from '@/components/system/confirmLogic.mjs'

const items = ref([])
const canApprove = ref(false)
const pending = ref(false)
const loaded = ref(null)
const error = ref('')
const busyId = ref(null)

const drafts = computed(() => items.value.filter((r) => r.status === 'draft').length)
const kindTitle = (kind) => OUTBOX_KIND[kind] || kind
const statusTitle = (status) => OUTBOX_STATUS[status] || status

async function load() {
  pending.value = true
  try {
    const data = await fetchOutbox()
    items.value = data.items || []
    canApprove.value = Boolean(data.can_approve)
    loaded.value = true
    error.value = ''
  } catch (e) {
    loaded.value = false
    error.value = e?.response?.data?.detail || e?.message || 'Не удалось загрузить письма'
  } finally {
    pending.value = false
  }
}

async function decide(row, approve) {
  busyId.value = row.id
  try {
    const updated = await decideOutbox(row.id, approve)
    items.value = items.value.map((r) => (r.id === row.id ? updated : r))
    error.value = ''
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || 'Не удалось сохранить решение'
  } finally {
    busyId.value = null
  }
}

onMounted(load)
</script>

<style scoped>
.ob { --ink: #1f2430; --muted: #6b7280; --line: #e7e5df; --gold: #f2b705; --red: #dc2626;
  padding: 18px 20px; border-radius: 20px; background: #fff; border: 1px solid var(--line);
  display: flex; flex-direction: column; gap: 12px; }
.ob__head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
.ob__title { margin: 0; font-size: 19px; color: var(--ink); display: flex; align-items: center; gap: 8px; }
.ob__badge { display: inline-grid; place-items: center; min-width: 24px; height: 24px; padding: 0 7px; border-radius: 999px;
  background: var(--red); color: #fff; font-size: 13px; font-weight: 800; }
.ob__lead { margin: 4px 0 0; font-size: 13.5px; color: var(--muted); max-width: 720px; }
.ob__refresh { border: 1px solid var(--line); background: #f7f5ef; border-radius: 999px; padding: 7px 14px; cursor: pointer; font-size: 13px; }
.ob__card { border: 1px solid var(--line); border-left: 5px solid var(--gold); border-radius: 14px; padding: 12px 14px; background: #fbfaf7; }
.ob__card--sent { border-left-color: #16a34a; opacity: .85; }
.ob__card--rejected { border-left-color: #9ca3af; opacity: .7; }
.ob__card--failed { border-left-color: var(--red); }
.ob__top { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.ob__kind { font-weight: 800; color: var(--ink); }
.ob__trip { color: #374151; font-size: 14px; }
.ob__status { margin-left: auto; font-size: 12px; font-weight: 700; padding: 3px 9px; border-radius: 999px; background: #fff3d6; color: #92400e; }
.ob__status--sent { background: #dcfce7; color: #166534; }
.ob__status--failed { background: #fee2e2; color: #991b1b; }
.ob__status--rejected { background: #f3f4f6; color: #4b5563; }
.ob__to, .ob__subject, .ob__who { margin: 6px 0 0; font-size: 13.5px; color: #374151; word-break: break-word; }
.ob__text { margin-top: 8px; }
.ob__text summary { cursor: pointer; font-size: 13px; color: var(--muted); }
.ob__text pre { white-space: pre-wrap; font-family: inherit; font-size: 13.5px; line-height: 1.5; margin: 8px 0 0;
  padding: 10px 12px; background: #fff; border: 1px solid var(--line); border-radius: 10px; }
.ob__actions { display: flex; gap: 8px; margin-top: 10px; }
.ob__btn { border: 0; border-radius: 999px; padding: 9px 16px; font-weight: 700; font-size: 14px; cursor: pointer; background: #f3f1ea; color: var(--ink); }
.ob__btn--send { background: linear-gradient(135deg, #ffe08a, var(--gold)); }
.ob__btn:disabled { opacity: .55; cursor: progress; }
.ob__error { margin: 0; color: var(--red); font-size: 13.5px; }
</style>
